import React, { useState, useEffect } from 'react';
import { X, User, MapPin, ArrowRight } from 'lucide-react';
import type {
  RepresentativeAssignment,
  CreateAssignmentRequest,
  UpdateAssignmentRequest,
} from '../../../types/assignments';

interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    data: CreateAssignmentRequest | UpdateAssignmentRequest
  ) => Promise<void>;
  assignment?: RepresentativeAssignment | null;
  title: string;
  zones: Array<{ id: string; name: string }>;
  unassignedUsers?: Array<{
    id: string;
    name: string;
    employeeNumber: string;
    email?: string;
  }>;
  mode: 'create' | 'edit';
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  assignment,
  title,
  zones,
  unassignedUsers = [],
  mode,
}) => {
  const [formData, setFormData] = useState({
    userId: '',
    zoneId: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = mode === 'edit';

  // Reset form cuando cambia el modal
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && assignment) {
        setFormData({
          userId: assignment.user.id,
          zoneId: assignment.assignment?.zoneId ?? '',
        });
      } else {
        setFormData({
          userId: '',
          zoneId: '',
        });
      }
      setErrors({});
    }
  }, [assignment, isOpen, isEditMode]);

  // Validación
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userId) {
      newErrors.userId = 'Debe seleccionar un representante';
    }

    if (!formData.zoneId) {
      newErrors.zoneId = 'Debe seleccionar una zona';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isEditMode) {
        // Solo enviar zoneId en modo edición
        const updateData: UpdateAssignmentRequest = {
          zoneId: formData.zoneId,
        };
        await onSave(updateData);
      } else {
        // Enviar userId y zoneId en modo creación
        const createData: CreateAssignmentRequest = {
          userId: formData.userId,
          zoneId: formData.zoneId,
        };
        await onSave(createData);
      }
      onClose();
    } catch (error: any) {
      console.error('Error saving assignment:', error);

      // Manejar errores específicos del servidor
      if (error.response?.data?.message) {
        if (error.response.data.message.includes('usuario')) {
          setErrors({ userId: 'El representante ya está asignado a una zona' });
        } else if (error.response.data.message.includes('zona')) {
          setErrors({ zoneId: 'Error con la zona seleccionada' });
        } else {
          setErrors({ submit: error.response.data.message });
        }
      } else {
        setErrors({
          submit: 'Error al guardar asignación. Intente nuevamente.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Change handler
  const handleChange = (field: string, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Limpiar error específico
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Función para cerrar con limpieza
  const handleClose = (): void => {
    setErrors({});
    onClose();
  };

  // Función para obtener nombre de zona
  const getZoneDisplayName = (zoneName: string): string => {
    const zoneNames = {
      NORTE: 'Norte',
      CENTRO: 'Centro',
      SUR: 'Sur',
    };
    return zoneNames[zoneName as keyof typeof zoneNames] || zoneName;
  };

  // Función para obtener el representante seleccionado actualmente
  const getSelectedUser = () => {
    if (isEditMode && assignment) {
      return assignment.user;
    }
    return unassignedUsers.find((user) => user.id === formData.userId);
  };

  if (!isOpen) return null;

  const selectedUser = getSelectedUser();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-lg w-full h-full sm:h-auto sm:max-w-md max-h-screen sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            {title}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-500 transition-colors disabled:opacity-50"
            title="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {/* Error general */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              <div className="font-medium">Error al guardar</div>
              <div className="text-sm">{errors.submit}</div>
            </div>
          )}

          {/* Representante */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Representante <span className="text-red-500">*</span>
            </label>

            {isEditMode ? (
              // En modo edición, mostrar el representante actual (no editable)
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <div className="flex items-center">
                  <User className="w-8 h-8 text-gray-400 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">
                      {assignment?.user.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {assignment?.user.email || 'Sin email'} • #
                      {assignment?.user.employeeNumber}
                    </div>
                    <div className="text-xs text-gray-500">
                      Estado:{' '}
                      {assignment?.user.status === 'active'
                        ? 'Activo'
                        : 'Inactivo'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // En modo creación, selector de representante
              <select
                value={formData.userId}
                onChange={(e) => handleChange('userId', e.target.value)}
                disabled={loading}
                className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-950 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50 ${
                  errors.userId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar representante sin asignar</option>
                {unassignedUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.employeeNumber}){' '}
                    {user.email ? `- ${user.email}` : ''}
                  </option>
                ))}
              </select>
            )}

            {errors.userId && (
              <p className="text-red-500 text-sm mt-1">{errors.userId}</p>
            )}

            {!isEditMode && unassignedUsers.length === 0 && (
              <p className="text-amber-600 text-sm mt-1">
                No hay representantes sin asignar. Todos los usuarios con rol
                REPRESENTATIVE ya tienen una zona asignada.
              </p>
            )}
          </div>

          {/* Zona */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zona a asignar <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.zoneId}
              onChange={(e) => handleChange('zoneId', e.target.value)}
              disabled={loading}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-950 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50 ${
                errors.zoneId ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Seleccionar zona</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {getZoneDisplayName(zone.name)}
                </option>
              ))}
            </select>
            {errors.zoneId && (
              <p className="text-red-500 text-sm mt-1">{errors.zoneId}</p>
            )}
          </div>

          {/* Vista previa de la asignación */}
          {selectedUser && formData.zoneId && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-3 flex items-center">
                <ArrowRight className="w-4 h-4 mr-1" />
                Vista previa de la asignación:
              </h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <User className="w-6 h-6 text-blue-600 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-blue-900">
                      {selectedUser.name}
                    </div>
                    <div className="text-xs text-blue-700">
                      #{selectedUser.employeeNumber}
                    </div>
                  </div>
                </div>

                <ArrowRight className="w-5 h-5 text-blue-400 mx-3" />

                <div className="flex items-center">
                  <MapPin className="w-6 h-6 text-blue-600 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-blue-900">
                      Zona{' '}
                      {getZoneDisplayName(
                        zones.find((z) => z.id === formData.zoneId)?.name || ''
                      )}
                    </div>
                    <div className="text-xs text-blue-700">
                      {isEditMode ? 'Nueva asignación' : 'Primera asignación'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Información adicional en modo edición */}
          {isEditMode && assignment?.isAssigned && assignment.assignment && (
            <div className="bg-gray-50 rounded-md p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Asignación actual:
              </h4>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>
                    <strong>Zona actual:</strong>{' '}
                    {getZoneDisplayName(assignment.assignment.zone.name)}
                  </span>
                </div>
                <p>
                  <strong>Asignado desde:</strong>{' '}
                  {new Date(
                    assignment.assignment.assignedAt
                  ).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={
                loading || (!isEditMode && unassignedUsers.length === 0)
              }
              className="px-6 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Guardando...
                </div>
              ) : isEditMode ? (
                'Cambiar Zona'
              ) : (
                'Asignar a Zona'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignmentModal;
